import { createFileRoute } from "@tanstack/react-router";
import { PhaseSheetArt } from "~/components/inside/how-we-work/phase-sheet";
import { InnerCta } from "~/components/inside/inner-cta";
import { PageIntro, PageLabel } from "~/components/inside/page-intro";
import { PageScene } from "~/components/inside/page-scene";
import { usePageEngagement } from "~/hooks/use-page-engagement";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { getPreparePoints, getProcessPhases } from "~/lib/constants/process";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getHowWeWorkPageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/how-we-work")({
  component: HowWeWorkPage,
  head: () => {
    const seoData = seo({
      description: m.meta_how_we_work_description(),
      keywords:
        "AI automation project, AI process automation, AI consulting, discovery, handover, human review, Lindevo",
      path: "/how-we-work",
      title: m.meta_how_we_work_title(),
    });
    const structuredData = generateStructuredDataGraph(
      getHowWeWorkPageSchemas()
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

/**
 * How an engagement runs, in four phases. Each one names what it leaves behind
 * and shows that artifact beside the text, so "we hand over cleanly" is a thing
 * the visitor can look at rather than a claim.
 */
function HowWeWorkPage() {
  usePageEngagement({ pagePath: "/how-we-work" });

  const phases = getProcessPhases();

  return (
    <>
      <PageScene variant="process">
        <PageLabel>{m.how_we_work_label()}</PageLabel>
        <h1>
          {m.how_we_work_title_1()} <br />
          {m.how_we_work_title_2()}
        </h1>
        <PageIntro>{m.how_we_work_intro()}</PageIntro>

        <ol aria-label={m.how_we_work_nav_label()} className="phase-nav">
          {phases.map((phase) => (
            <li key={phase.id}>
              <a href={`#${phase.id}`}>
                <span>{phase.navStage}</span>
                <strong>{phase.navTitle}</strong>
              </a>
            </li>
          ))}
        </ol>
      </PageScene>

      <div className="wrap">
        {phases.map((phase) => (
          <section className="phase" id={phase.id} key={phase.id}>
            {/* The stage line in the nav already announces the number. */}
            <span aria-hidden="true" className="phase-number">
              {phase.number}
            </span>

            <div className="phase-text">
              <h2>
                {phase.title[0]} <br />
                {phase.title[1]}
              </h2>
              {phase.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              <div className="deliverable">
                <span>{phase.deliverable.label}</span>
                <strong>{phase.deliverable.value}</strong>
              </div>
            </div>

            <div>
              <PhaseSheetArt sheet={phase.sheet} />
            </div>
          </section>
        ))}

        <section className="prepare">
          <div>
            <h2>
              {m.how_we_work_prepare_title_1()} <br />
              {m.how_we_work_prepare_title_2()}
            </h2>
            <p>{m.how_we_work_prepare_body()}</p>
          </div>
          <ul className="prepare-list">
            {getPreparePoints().map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>
      </div>

      <InnerCta
        lede={m.how_we_work_cta_lede()}
        note={m.how_we_work_cta_note()}
        title={m.how_we_work_cta_title()}
      />
    </>
  );
}
